import {
  isTemplateWithFlow,
  isTemplateWithRequestContactInfo,
} from '@src/components/AdminHome/Channels/WhatsApp/WhatsAppFlows/utils';
import { SpecialMessageParser } from '@src/logic/specialmessageprocessing';
import { MediaType } from '@src/model/frontendmodel';
import { TimelineEvent, TimelineEventIntention } from '@src/types';
import { useMemo } from 'react';

const useChatMessageFlags = (message: TimelineEvent) => {
  // AI follow-up messages are sent by the system on behalf of the agent, not
  // by a bot. Without this override, messageFromBot=true would suppress the
  // delivery-status checkmarks (showMessageStatus) and apply bot styling,
  // breaking the visual feedback loop for agents monitoring AI follow-ups.
  // Derive locally instead of mutating the argument to preserve referential
  // stability of the original message object.
  const messageFromBot = message.isAiFollowup ? false : message.messageFromBot;

  const hasTextMsg = useMemo(
    () => !message?.mediaType || message?.mediaType === MediaType.TEXT,
    [message]
  );

  const notDeliveredNotifyData = useMemo(
    () =>
      hasTextMsg &&
      SpecialMessageParser.isMsgNotDeliveredNotification(message?.text),
    [message, hasTextMsg]
  );

  const isIncomingMsg = useMemo(
    () => message?.direction === 'INCOMING' && !notDeliveredNotifyData,
    [message.direction, notDeliveredNotifyData]
  );

  const isOutgoingMsg = useMemo(
    () => message?.direction === 'OUTGOING' || !!notDeliveredNotifyData,
    [message.direction, notDeliveredNotifyData]
  );

  const isStartTemplate = useMemo(
    () =>
      message?.startTemplate ||
      message?.intention === TimelineEventIntention.START_CHAT,
    [message?.startTemplate, message?.intention]
  );

  const isResumeTemplate = useMemo(
    () =>
      message?.resumeTemplate ||
      message?.intention === TimelineEventIntention.RESUME_CHAT,
    [message?.resumeTemplate, message?.intention]
  );

  const isStartCallPermission = useMemo(
    () => message.intention === TimelineEventIntention.REQUEST_CALL_PERMISSION,
    [message.intention]
  );

  const isTemplateFlow = useMemo(
    () =>
      message?.mediaType === MediaType.TEMPLATE && isTemplateWithFlow(message),
    [message]
  );

  const isTemplate = useMemo(
    () =>
      (message?.isBroadcastWapMsg ||
        isStartTemplate ||
        isResumeTemplate ||
        message?.mediaType === MediaType.TEMPLATE ||
        message.intention === TimelineEventIntention.START_CHAT ||
        message.intention === TimelineEventIntention.RESUME_CHAT) &&
      !isTemplateFlow &&
      !isStartCallPermission,
    [
      message?.isBroadcastWapMsg,
      isStartTemplate,
      isResumeTemplate,
      message?.mediaType,
      message?.intention,
      isTemplateFlow,
      isStartCallPermission,
    ]
  );

  // Request-phone-number template (REQUEST_CONTACT_INFO button). Derived from the
  // message content, like the flow template above: the backend sends these through
  // the broadcast path without a start/resume intention. The bubble keeps the regular
  // template styling (isTemplate stays true); only the header changes. A start or
  // resume send that happens to use such a template keeps its own header, so two
  // titles never stack; flow and call-permission are already excluded by isTemplate.
  const isRequestContactInfoTemplate = useMemo(
    () =>
      isTemplate &&
      !isStartTemplate &&
      !isResumeTemplate &&
      message?.mediaType === MediaType.TEMPLATE &&
      isTemplateWithRequestContactInfo(message),
    [isTemplate, isStartTemplate, isResumeTemplate, message]
  );

  const isAutomatic = useMemo(
    () => message?.isAutomaticMessage,
    [message?.isAutomaticMessage]
  );

  const isAiFollowup = useMemo(
    () => message?.type === 'AI_FOLLOWUP_MESSAGE' || message?.isAiFollowup,
    [message?.type, message?.isAiFollowup]
  );

  const isBot = useMemo(
    () =>
      messageFromBot &&
      !isStartTemplate &&
      !isResumeTemplate &&
      message?.intention !== TimelineEventIntention.START_CHAT &&
      message?.intention !== TimelineEventIntention.RESUME_CHAT,
    [messageFromBot, isStartTemplate, isResumeTemplate, message?.intention]
  );

  const isSticker = useMemo(
    () => message?.type === MediaType.STICKER,
    [message?.type]
  );

  const isQuoted = useMemo(() => !!message?.quotedMessage, [message]);

  const isQuotedFromBotButtons = useMemo(
    () => !!message?.quotedMessage?.agentChatButtons?.buttons?.length,
    [message]
  );

  return {
    hasTextMsg,
    notDeliveredNotifyData,
    isIncoming: isIncomingMsg,
    isOutgoing: isOutgoingMsg,
    isTemplate, // Corresponds to is-broadcast class
    isAutomatic,
    isAiFollowup,
    isBot,
    isSticker,
    isQuoted,
    isQuotedFromBotButtons,
    isStartCallPermission,
    isTemplateFlow,
    isTemplateCallPermission: isStartCallPermission,
    isStartTemplate,
    isResumeTemplate,
    isRequestContactInfoTemplate,
  };
};

export default useChatMessageFlags;
